import React, { useEffect, useState, useCallback } from "react";
import {
  Play, Lock, Crown, Star, Check, ChevronDown, ChevronRight, Loader2, ArrowRight, ShoppingCart,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import GameCanvas from "@/components/GameCanvas";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const TYPE_ICON = { boss: Crown, bonus: Star, secret: Lock };

export default function Campaign({ gameId, game, onLeaderboard, onPurchased }) {
  const [meta, setMeta] = useState(null);
  const [levels, setLevels] = useState([]);
  const [progress, setProgress] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [active, setActive] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingPlay, setLoadingPlay] = useState(false);
  const [buying, setBuying] = useState(false);
  const [view, setView] = useState("map");

  const load = useCallback(async () => {
    try {
      const [l, p] = await Promise.all([
        api.get(`/arcade/${gameId}/levels`),
        api.get(`/arcade/${gameId}/progress`),
      ]);
      setMeta(l.data.meta); setLevels(l.data.levels); setProgress(p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [gameId]);
  useEffect(() => { load(); }, [load]);

  const monetization = game?.monetization || {};
  const entitled = !!(progress?.entitled || game?.entitled);
  const unlocked = progress?.unlocked_index || 1;
  const completed = new Set(progress?.completed || []);

  // Handle Stripe return for a level-pack purchase
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("pack_session");
    if (!sid) return;
    let tries = 0, stop = false;
    const clean = () => {
      const u = new URL(window.location.href); u.searchParams.delete("pack_session");
      window.history.replaceState({}, "", u.toString());
    };
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/arcade/${gameId}/levels/checkout/status/${sid}`);
        if (data.entitled) {
          toast.success("Premium pack unlocked! 🎉 All levels are yours.");
          clean(); await load(); onPurchased && onPurchased(); return;
        }
        if (data.status === "expired") { toast.error("Payment session expired."); clean(); return; }
      } catch (e) { /* keep polling */ }
      if (++tries < 8) setTimeout(poll, 1500); else clean();
    };
    poll();
    return () => { stop = true; };
  }, [gameId]); // eslint-disable-line

  const buyPack = async () => {
    setBuying(true);
    try {
      const { data } = await api.post(`/arcade/${gameId}/levels/checkout`, { origin_url: window.location.origin });
      if (data.requires_approval) {
        toast.success(data.message || "Sent to your parent/guardian for approval 🛡️");
        setBuying(false); return;
      }
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBuying(false); }
  };

  const levelStatus = (lvl) => {
    if (completed.has(lvl.index)) return "completed";
    const isPrem = lvl.access === "premium" || lvl.access === "locked";
    if (isPrem && !entitled) return "premium";
    if (lvl.index <= unlocked) return "playable";
    return "locked";
  };

  const playLevel = async (lvl) => {
    setLoadingPlay(true); setResult(null);
    try {
      const { data } = await api.get(`/arcade/${gameId}/levels/${lvl.id}/play`);
      setActive({ config: data.config, level: data.level });
      api.post("/quests/track", { event: "play" }).catch(() => {});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      if (e.response?.status === 402) toast.error("This level is part of a premium pack — unlocking comes soon!");
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoadingPlay(false); }
  };

  const onGameOver = async (d) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/arcade/${gameId}/levels/${active.level.id}/complete`,
        { score: d.score || 0, stars: 3 });
      setProgress(data.progress);
      if (onLeaderboard && data.leaderboard) onLeaderboard(data.leaderboard);
      const next = levels.find((x) => x.index === active.level.index + 1);
      setResult({ score: d.score || 0, rewards: data.rewards, first: data.first_time, next });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exitToLevels = () => { setActive(null); setResult(null); load(); };
  const startCampaign = () => { const l = levels.find((x) => x.index === 1); if (l) playLevel(l); };
  const continueCampaign = () => {
    const t = levels.find((x) => x.index === unlocked && levelStatus(x) === "playable")
      || levels.find((x) => levelStatus(x) === "playable");
    if (t) playLevel(t);
  };

  if (!meta) {
    return <div className="h-72 grid place-items-center rounded-2xl border border-[hsl(var(--border))]" data-testid="campaign-loading"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div>;
  }

  // ── Playing a level ──
  if (active) {
    return (
      <div data-testid="campaign-play">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="font-bold flex items-center gap-2 min-w-0">
            <span className="truncate">{active.level.name}</span>
            <Badge variant="outline" className="text-[10px] shrink-0">Lvl {active.level.index}</Badge>
            <Badge variant="outline" className="text-[10px] shrink-0">Diff {active.level.difficulty}/10</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={exitToLevels} data-testid="campaign-exit-btn">Exit to levels</Button>
        </div>
        <GameCanvas config={active.config} onGameOver={onGameOver} />
        {result && (
          <div className="mt-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 text-center" data-testid="campaign-result">
            <div className="text-lg font-bold">{result.first ? "Level Complete! 🎉" : "Level Replayed"}</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
              Score: {result.score}
              {result.first && result.rewards?.coins ? ` · +${result.rewards.coins} coins · +${result.rewards.xp} XP` : ""}
            </div>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {result.next && (
                <Button onClick={() => playLevel(result.next)} disabled={loadingPlay} data-testid="campaign-next-btn">
                  Next: {result.next.name} <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              <Button variant="secondary" onClick={exitToLevels} data-testid="campaign-back-btn">Back to levels</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Overview ──
  const worlds = meta.worlds || [];
  const total = meta.count || 0;
  const completedCount = completed.size;
  const pct = total ? Math.round((completedCount / total) * 100) : 0;
  const canContinue = unlocked > 1 || completedCount > 0;

  return (
    <div data-testid="campaign-overview">
      <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden mb-4">
        <div className="p-6" style={{ background: `linear-gradient(135deg, ${worlds[0]?.palette?.bg || "#0b1026"}, ${worlds[0]?.palette?.accent || "#1f6feb"})` }}>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="lg" onClick={startCampaign} disabled={loadingPlay} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="campaign-play-btn">
              {loadingPlay ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5 fill-black" />} Play Level 1
            </Button>
            {canContinue && (
              <Button size="lg" variant="secondary" onClick={continueCampaign} disabled={loadingPlay} className="rounded-full" data-testid="campaign-continue-btn">
                <ArrowRight className="w-5 h-5" /> Continue · Level {unlocked}
              </Button>
            )}
          </div>
          <div className="mt-4 max-w-md">
            <div className="flex justify-between text-xs text-white/80 mb-1"><span>{completedCount} / {total} levels</span><span>{pct}%</span></div>
            <div className="h-2 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-white transition-all" style={{ width: `${pct}%` }} data-testid="campaign-progress-bar" /></div>
          </div>
        </div>
        <div className="grid grid-cols-4 divide-x divide-[hsl(var(--border))] bg-[hsl(var(--card))]">
          <Stat label="Levels" value={total} />
          <Stat label="Free" value={meta.free_count || 0} />
          <Stat label="Premium" value={Math.max(0, total - (meta.free_count || 0))} />
          <Stat label="Worlds" value={worlds.length} />
        </div>
      </div>

      {monetization.for_sale && !entitled && monetization.premium_count > 0 && (
        <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-4 mb-4 flex items-center gap-4" data-testid="campaign-unlock-banner">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 grid place-items-center shrink-0"><Crown className="w-6 h-6 text-amber-500" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{monetization.pack_title}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">Unlock all {monetization.premium_count} premium levels — one-time purchase.</div>
          </div>
          <Button onClick={buyPack} disabled={buying} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white shrink-0" data-testid="campaign-unlock-btn">
            {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} Unlock ${monetization.price}
          </Button>
        </div>
      )}
      {entitled && monetization.premium_count > 0 && (
        <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-2 mb-4 text-sm font-semibold text-green-500 flex items-center gap-2" data-testid="campaign-owned-badge">
          <Check className="w-4 h-4" /> Premium pack unlocked — all {monetization.premium_count} premium levels available.
        </div>
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-lg font-bold">World map</h2>
        <div className="flex gap-1 rounded-full border border-[hsl(var(--border))] p-0.5" data-testid="campaign-view-toggle">
          {["map", "list"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`campaign-view-${v}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${view === v ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>{v}</button>
          ))}
        </div>
      </div>
      <Legend />

      {view === "map" ? (
        <MapView worlds={worlds} levels={levels} levelStatus={levelStatus} unlocked={unlocked}
          loadingPlay={loadingPlay} buying={buying}
          onPlay={playLevel} onBuy={buyPack} packTitle={monetization.pack_title} />
      ) : (
      <div className="space-y-3" data-testid="campaign-worlds">
        {worlds.map((w) => {
          const open = expanded[w.index] ?? (w.index === 1);
          const wl = levels.filter((l) => l.world_index === w.index);
          return (
            <div key={w.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
              <button onClick={() => setExpanded((e) => ({ ...e, [w.index]: !open }))} className="w-full flex items-center gap-3 p-4 text-left hover:bg-[hsl(var(--muted))]/30 transition-colors" data-testid={`campaign-world-${w.index}`}>
                {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                <div className="flex gap-1 shrink-0">{["bg", "ground", "accent"].map((c) => <span key={c} className="w-3 h-6 rounded" style={{ background: w.palette?.[c] }} />)}</div>
                <div className="flex-1 min-w-0"><div className="font-bold truncate">World {w.index}: {w.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{w.theme}</div></div>
                <span className="text-xs text-[hsl(var(--muted-foreground))] shrink-0 tabular-nums">{w.level_count} lvl</span>
              </button>
              {open && (
                <div className="px-4 pb-4 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2" data-testid={`campaign-world-grid-${w.index}`}>
                  {wl.map((l) => {
                    const st = levelStatus(l);
                    const Icon = TYPE_ICON[l.type];
                    const isPrem = st === "premium";
                    const clickable = (st === "playable" || st === "completed") && !loadingPlay;
                    return (
                      <button key={l.id} disabled={(!clickable && !isPrem) || buying} onClick={() => (isPrem ? buyPack() : playLevel(l))}
                        title={isPrem ? `Unlock with ${monetization.pack_title || "the premium pack"}` : l.name}
                        className={`relative aspect-square rounded-xl border flex items-center justify-center text-sm font-bold transition-all ${
                          st === "completed" ? "border-green-500/50 bg-green-500/10 text-green-400 hover:scale-105" :
                          st === "playable" ? "border-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:scale-105" :
                          st === "premium" ? "border-amber-500/40 bg-amber-500/5 text-amber-500/80 hover:scale-105 hover:bg-amber-500/15" :
                          "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 text-[hsl(var(--muted-foreground))] opacity-60 cursor-not-allowed"
                        }`} data-testid={`campaign-level-${l.index}`}>
                        {st === "completed" ? <Check className="w-4 h-4" /> :
                          st === "premium" ? <Crown className="w-4 h-4" /> :
                          st === "locked" ? <Lock className="w-3.5 h-3.5" /> :
                          <span>{l.local_index}</span>}
                        {Icon && st === "locked" && <Icon className="w-3 h-3 absolute top-1 right-1 text-amber-400/70" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-4 text-[11px] text-[hsl(var(--muted-foreground))]" data-testid="campaign-legend">
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-full bg-green-500/70 inline-block" /> Completed</span>
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-full bg-[hsl(var(--primary))] inline-block" /> Playable</span>
    <span className="flex items-center gap-1.5"><Crown className="w-3 h-3 text-amber-500" /> Premium</span>
    <span className="flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-rose-400" /> Boss</span>
    <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> Locked</span>
  </div>
);

function MapNode({ l, st, isCurrent, onClick, disabled }) {
  const boss = l.type === "boss";
  const cls = st === "completed" ? "border-green-500/60 bg-green-500/15 text-green-400"
    : st === "playable" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
    : st === "premium" ? "border-amber-500/50 bg-amber-500/10 text-amber-500"
    : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))] opacity-70";
  const size = boss ? "w-14 h-14" : "w-11 h-11";
  return (
    <div className="relative flex flex-col items-center shrink-0">
      {isCurrent && <span className="absolute -top-5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white animate-bounce">YOU</span>}
      <button onClick={onClick} disabled={disabled}
        title={st === "premium" ? "Unlock with the premium pack" : l.name}
        data-testid={`campaign-map-node-${l.index}`}
        className={`${size} rounded-full border-2 grid place-items-center text-sm font-extrabold transition-transform shadow-md ${cls} ${disabled ? "cursor-not-allowed" : "hover:scale-110 active:scale-95"} ${isCurrent ? "ring-4 ring-[hsl(var(--primary))]/30" : ""}`}>
        {boss ? <Crown className={`${st === "locked" || st === "premium" ? "" : "fill-current"} w-5 h-5`} />
          : st === "completed" ? <Check className="w-5 h-5" />
          : st === "premium" ? <Crown className="w-4 h-4" />
          : st === "locked" ? <Lock className="w-4 h-4" />
          : <span>{l.local_index}</span>}
      </button>
    </div>
  );
}

const Connector = () => <span className="h-0.5 w-5 sm:w-8 shrink-0 rounded-full bg-[hsl(var(--border))] [background-image:repeating-linear-gradient(90deg,currentColor_0_4px,transparent_4px_8px)] text-[hsl(var(--muted-foreground))]/40" />;

function MapView({ worlds, levels, levelStatus, unlocked, loadingPlay, buying, onPlay, onBuy }) {
  return (
    <div className="space-y-4" data-testid="campaign-map">
      {worlds.map((w) => {
        const wl = levels.filter((l) => l.world_index === w.index);
        const rows = chunk(wl, 6);
        return (
          <div key={w.id} className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
            <div className="p-4 flex items-center gap-3" style={{ background: `linear-gradient(135deg, ${w.palette?.bg || "#0b1026"}33, ${w.palette?.accent || "#1f6feb"}22)` }}>
              <div className="flex gap-1 shrink-0">{["bg", "ground", "accent"].map((c) => <span key={c} className="w-2.5 h-6 rounded" style={{ background: w.palette?.[c] }} />)}</div>
              <div className="min-w-0"><div className="font-bold truncate">World {w.index}: {w.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{w.theme}</div></div>
            </div>
            <div className="p-4 sm:p-6 space-y-3 bg-[hsl(var(--card))]">
              {rows.map((row, ri) => (
                <div key={ri} className={`flex items-center ${ri % 2 ? "flex-row-reverse" : ""}`}>
                  {row.map((l, ci) => {
                    const st = levelStatus(l);
                    const isPrem = st === "premium";
                    const isCurrent = st === "playable" && l.index === unlocked;
                    const clickable = (st === "playable" || st === "completed") && !loadingPlay;
                    return (
                      <React.Fragment key={l.id}>
                        {ci > 0 && <Connector />}
                        <MapNode l={l} st={st} isCurrent={isCurrent}
                          disabled={(!clickable && !isPrem) || buying}
                          onClick={() => (isPrem ? onBuy() : onPlay(l))} />
                      </React.Fragment>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="p-3 text-center">
    <div className="text-lg font-bold tabular-nums">{value}</div>
    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{label}</div>
  </div>
);
